import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, ButtonGroup, IconButton, Text, TextButton } from '@zillow/constellation-10';
import { IconCloseFilled } from '@zillow/constellation-icons';

import { TOUR_PLACEMENT } from '../../../constants/FeatureTourConstants';

import { popoverBodyStyles, popoverCloseButtonStyles, popoverFooterStyles, popoverStyles } from '../styles';

import type { FeatureTourStep, Rect, TourPlacement } from '..';

type Props = {
  step: FeatureTourStep;
  index: number;
  total: number;
  targetRect: Rect;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onSkip: () => void;
};

const POPOVER_WIDTH = 355;
// fallback for initial render
const DEFAULT_POPOVER_HEIGHT = 180;
const GAP = 24;
const VIEWPORT_PADDING = 16;

type Position = { top: number; left: number };

// Calculate position for a specific placement
const getPositionForPlacement = (target: Rect, placement: TourPlacement, popoverHeight: number): Position => {
  const centerX = target.left + target.width / 2;
  const centerY = target.top + target.height / 2;

  switch (placement) {
    // Popover appears above target, horizontally centered
    case TOUR_PLACEMENT.TOP_CENTER:
      return {
        top: target.top - popoverHeight - GAP,
        left: centerX - POPOVER_WIDTH / 2,
      };
    // Popover appears above target, right edge aligned with target's right edge
    case TOUR_PLACEMENT.TOP_RIGHT:
      return {
        top: target.top - popoverHeight - GAP,
        left: target.left + target.width - POPOVER_WIDTH,
      };
    // Popover appears above target, left edge aligned with target's left edge
    case TOUR_PLACEMENT.TOP_LEFT:
      return {
        top: target.top - popoverHeight - GAP,
        left: target.left,
      };
    // Popover appears below target, horizontally centered
    case TOUR_PLACEMENT.BOTTOM_CENTER:
      return {
        top: target.top + target.height + GAP,
        left: centerX - POPOVER_WIDTH / 2,
      };
    // Popover appears below target, right edge aligned with target's right edge
    case TOUR_PLACEMENT.BOTTOM_RIGHT:
      return {
        top: target.top + target.height + GAP,
        left: target.left + target.width - POPOVER_WIDTH,
      };
    // Popover appears below target, left edge aligned with target's left edge
    case TOUR_PLACEMENT.BOTTOM_LEFT:
      return {
        top: target.top + target.height + GAP,
        left: target.left,
      };
    // Popover appears to the left of target, vertically centered
    case TOUR_PLACEMENT.LEFT:
      return {
        top: centerY - popoverHeight / 2,
        left: target.left - POPOVER_WIDTH - GAP,
      };
    // Popover appears to the right of target, vertically centered
    case TOUR_PLACEMENT.RIGHT:
      return {
        top: centerY - popoverHeight / 2,
        left: target.left + target.width + GAP,
      };
    // Default to bottom-center for any unhandled placement
    default:
      return {
        top: target.top + target.height + GAP,
        left: centerX - POPOVER_WIDTH / 2,
      };
  }
};

// check if a position fits within viewport without clipping
const fitsInViewport = (pos: Position, popoverHeight: number): boolean => {
  return (
    pos.top >= VIEWPORT_PADDING &&
    pos.left >= VIEWPORT_PADDING &&
    pos.top + popoverHeight <= window.innerHeight - VIEWPORT_PADDING &&
    pos.left + POPOVER_WIDTH <= window.innerWidth - VIEWPORT_PADDING
  );
};

// clamp position to fit within viewport
const clampToViewport = (pos: Position, popoverHeight: number): Position => {
  return {
    top: Math.max(VIEWPORT_PADDING, Math.min(pos.top, window.innerHeight - popoverHeight - VIEWPORT_PADDING)),
    left: Math.max(VIEWPORT_PADDING, Math.min(pos.left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING)),
  };
};

// auto-placement: try preferred placement first, then find best alternative
const computePopoverPosition = (target: Rect, preferredPlacement: TourPlacement, popoverHeight: number) => {
  const placements: Array<TourPlacement> = [
    TOUR_PLACEMENT.BOTTOM_CENTER,
    TOUR_PLACEMENT.RIGHT,
    TOUR_PLACEMENT.LEFT,
    TOUR_PLACEMENT.TOP_CENTER,
  ];

  // try preferred placement first
  const preferredPos = getPositionForPlacement(target, preferredPlacement, popoverHeight);

  if (fitsInViewport(preferredPos, popoverHeight)) {
    return { ...preferredPos, width: POPOVER_WIDTH };
  }

  // Try other placements in order of preference
  for (const placement of placements) {
    if (placement === preferredPlacement) continue;
    const pos = getPositionForPlacement(target, placement, popoverHeight);
    if (fitsInViewport(pos, popoverHeight)) {
      return { ...pos, width: POPOVER_WIDTH };
    }
  }

  // No placement fits perfectly, clamp preferred placement to viewport
  const clampedPos = clampToViewport(preferredPos, popoverHeight);
  return { ...clampedPos, width: POPOVER_WIDTH };
};

// TODO: handle skip - persist in DB
export function TourPopover({ step, index, total, targetRect, onNext, onPrev, onSkip }: Props) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(DEFAULT_POPOVER_HEIGHT);

  const placement = step.placement ?? TOUR_PLACEMENT.BOTTOM_CENTER;
  const offset = step.popoverOffset;

  // Measure actual popover height after render
  useLayoutEffect(() => {
    if (popoverRef.current) {
      const height = popoverRef.current.getBoundingClientRect().height;
      if (height > 0 && height !== measuredHeight) {
        setMeasuredHeight(height);
      }
    }
  }, [step.title, step.body, measuredHeight]);

  const style = useMemo(() => {
    const pos = computePopoverPosition(targetRect, placement, measuredHeight);

    // Apply optional offset
    const finalTop = pos.top + (offset?.top ?? 0);
    const finalLeft = pos.left + (offset?.left ?? 0);

    return { position: 'absolute' as const, left: finalLeft, top: finalTop, width: pos.width };
  }, [targetRect, placement, offset, measuredHeight]);

  const isFirst = index === 0;
  const isLast = index === total - 1;

  return (
    <Box ref={popoverRef} css={popoverStyles} style={style}>
      <Box css={popoverCloseButtonStyles}>
        <IconButton
          title="Close tour"
          tone="neutral"
          emphasis="bare"
          size="sm"
          onClick={onSkip}
          css={{ borderRadius: '50%' }}
        >
          <IconCloseFilled />
        </IconButton>
      </Box>

      {step.title && (
        <Text textStyle="body-lg-bold" asChild>
          <p>{step.title}</p>
        </Text>
      )}

      <Box css={{ ...popoverBodyStyles, marginBottom: '800' }}>
        <Text textStyle="body" asChild>
          <p>{step.body}</p>
        </Text>
      </Box>

      <Box css={popoverFooterStyles}>
        <ButtonGroup aria-label="Tour navigation" css={{ flexGrow: 1, justifyContent: 'space-between' }}>
          {!isFirst && (
            <TextButton
              tone="brand"
              onClick={onPrev}
              textStyle="body-bold"
              css={{ paddingLeft: '400', paddingRight: '400' }}
            >
              Back
            </TextButton>
          )}

          <Button tone="brand" emphasis="filled" onClick={onNext} css={{ flex: 1, marginLeft: 'auto' }}>
            {isLast ? 'Done' : `Next: (Step ${index + 1} of ${total})`}
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
}
